import { TableRow } from "@carbon6/c6-frontend-design";
import type { TrainingGpuQuotaRequest, TrainingGpuQuotaRequestStatus } from "@/api";
import { compareNumericNullLast } from "@/components/table/compareNumeric";
import { parseUtcMs } from "@/utils/datetime";
import { resolveWorkspaceDisplayName } from "./gpuQuotaFormat";

// 상태 정렬의 순서. 처리할 것이 앞에 온다. 기본 비교는 wire 문자열의 알파벳 순서를 쓴다. 그
// 순서는 화면의 한국어 라벨과 무관하다.
const STATUS_SORT_RANK: Record<TrainingGpuQuotaRequestStatus, number> = {
  pending: 0,
  applied: 1,
  rejected: 2,
  cancelled: 3,
};

// 요청 이력 한 행. 가능한 동작(canCancel/canResolve)은 서버가 actor 기준으로 산출한 값을 그대로
// 쓴다. 표시명을 비교해 "내 요청인지" 추측하면 이름이 같은 다른 사람을 구분하지 못한다.
export class GpuQuotaRequestTableRow extends TableRow {
  readonly requestUuid: string;
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly requestedGpuLimit: number;
  // 증액 기간(일). 기간 없이 종결된 옛 요청은 null 이다.
  readonly requestedDays: number | null;
  // 승인된 요청의 증액이 끝나는 시각(ms). 서버가 기록한 실제 종료 시각(grant_end_at)이다. 승인되지
  // 않은 요청과 종료 시각이 기록되지 않은 옛 승인 요청은 null 이다.
  readonly grantEndsAtMs: number | null;
  readonly reason: string;
  readonly status: TrainingGpuQuotaRequestStatus;
  // 대상 workspace 를 지금 가진 사람. 선점자가 없으면 null 이다. 접수자와 다르므로 따로 보관한다.
  readonly ownerName: string | null;
  readonly requestedBy: string;
  readonly resolvedBy: string | null;
  readonly resolutionNote: string | null;
  readonly createdAt: string;
  readonly canCancel: boolean;
  readonly canResolve: boolean;

  constructor(request: TrainingGpuQuotaRequest) {
    super(null);
    this.requestUuid = request.uuid;
    this.workspaceId = request.training_workspace_id;
    this.workspaceName = resolveWorkspaceDisplayName(
      request.workspace_display_name,
      request.training_workspace_id,
    );
    this.requestedGpuLimit = request.requested_gpu_limit;
    this.requestedDays = request.requested_days;
    this.grantEndsAtMs = parseUtcMs(request.grant_end_at);
    this.reason = request.reason;
    this.status = request.status;
    this.ownerName = request.owner_member?.display_name ?? null;
    // member 가 삭제되면 표시명만 빈다. 그래서 "알 수 없음" 으로 표시한다(요청은 남는다).
    this.requestedBy = request.requested_by ?? "—";
    this.resolvedBy = request.resolved_by;
    this.resolutionNote = request.resolution_note;
    this.createdAt = request.created_at;
    this.canCancel = request.can_cancel;
    this.canResolve = request.can_resolve;
  }

  getRowId(): string {
    return this.requestUuid;
  }

  // 숫자·상태·시각 컬럼은 셀 문자열이 아니라 값으로 비교한다. 문자열로 비교하면 한도 "8" 이
  // "16" 뒤에 온다.
  override compare(other: this, columnKey: string): number {
    switch (columnKey) {
      case "requestedGpuLimit":
        return compareNumericNullLast(this.requestedGpuLimit, other.requestedGpuLimit);
      case "requestedDays":
        return compareNumericNullLast(this.requestedDays, other.requestedDays);
      case "status":
        return STATUS_SORT_RANK[this.status] - STATUS_SORT_RANK[other.status];
      case "createdAt":
        return compareNumericNullLast(parseUtcMs(this.createdAt), parseUtcMs(other.createdAt));
      default:
        return super.compare(other, columnKey);
    }
  }

  // 모든 행을 표시한다. 가시성은 서버의 조직 스코프가 정한다.
  canShow(): boolean {
    return true;
  }

  // 선택 UI 가 없는 목록이므로 선택 대상이 아니다.
  canSelect(): boolean {
    return false;
  }
}

export function createGpuQuotaRequestTableRows(
  requests: readonly TrainingGpuQuotaRequest[],
): GpuQuotaRequestTableRow[] {
  return requests.map((request) => new GpuQuotaRequestTableRow(request));
}
